import React, { createContext, ReactNode, useContext, useState } from "react";
import {
    AppBar, Box,
    Button,
    Dialog, Icon,
    IconButton,
    Slide,
    Toolbar,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { t } from "i18next";

export const useConfirm = () => {
    const [, setConfirm] = useContext(ConfirmContext);

    return (props: ConfirmProps) => {
        setConfirm({ isOpen: true, props: { ...defaultValue.props, ...props } });
    };
};

export enum ConfirmType {
    Save,
    Remove,
}

export interface ConfirmProps {
    title: string;
    onSubmit: () => void;
    submitText?: string;
    cancelText?: string;
    icon?: React.ElementType;
    template?: ReactNode;
    type?: ConfirmType;
}

interface ConfirmContextValue {
    isOpen: boolean;
    props: ConfirmProps;
}

type SetConfirmContextValue = React.Dispatch<React.SetStateAction<ConfirmContextValue>>;

const defaultValue: ConfirmContextValue = {
    isOpen: false,
    props: {
        title: "",
        onSubmit: () => null,
        submitText: t("common.ok"),
        cancelText: t("common.cancel"),
        icon: null,
        template: null,
        type: ConfirmType.Save,
    },
};

export const ConfirmContext = createContext<[ConfirmContextValue, SetConfirmContextValue]>([defaultValue, () => null]);

export const ConfirmContextProvider = ({ children }: { children: ReactNode }) => {
    const [prompt, setPrompt] = useState<ConfirmContextValue>(defaultValue);

    return (
        <ConfirmContext.Provider value={[prompt, setPrompt]}>
            {children}
        </ConfirmContext.Provider>
    );
};

export const Confirm = () => {
    const [confirm, setConfirm] = useContext(ConfirmContext);
    const { isOpen, props: { icon, title, template, submitText, cancelText, type, onSubmit } } = confirm;
    const theme = useTheme();

    const closeConfirm = () => setConfirm({ ...confirm, isOpen: false });

    const submit = () => {
        onSubmit();
        closeConfirm();
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Dialog
            fullScreen
            open={isOpen}
            onClose={closeConfirm}
            TransitionComponent={Slide}
        >
            <AppBar sx={{ bgcolor: theme.palette.white.main, boxShadow: "none" }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={closeConfirm}
                        aria-label="close"
                    >
                        <CloseIcon color="action" />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Box
                px={4}
                sx={{ height: "100%", textAlign: "center" }}
                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
            >
                {icon &&
                    <Icon
                        component={icon}
                        sx={{ fontSize: 80 }}
                        color={type === ConfirmType.Save ? "success" : "error"}
                    />
                }
                <Typography
                    fontSize={40}
                    color="blue.dark"
                    sx={{ lineHeight: 1.2, fontWeight: 600 }}
                    mt={5}
                >
                    {title}
                </Typography>

                {template &&
                    <Typography
                        fontSize={24}
                        color="blue.dark"
                        mt={3}
                    >
                        {template}
                    </Typography>
                }

                <Box mt={7} sx={{ width: "100%" }}>
                    <Button
                        onClick={submit}
                        color={type === ConfirmType.Save ? "success" : "error"}
                        variant="contained"
                        style={{ fontSize: 18, borderRadius: "10px" }}
                    >
                        {submitText}
                    </Button>
                </Box>
                <Box
                    mt={4} mb={5}
                    sx={{ width: "100%" }}
                >
                    <Button
                        onClick={closeConfirm}
                        variant="contained"
                        style={{ fontSize: 18, backgroundColor: theme.palette.gray.light, color: theme.palette.dark.main, borderRadius: "10px" }}
                    >
                        {cancelText}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};
